/**
 * AnalyticsInsights — Pillar & Format Performance-Insights in Alltagssprache
 *
 * Zeigt dem Kunden, welche Inhaltsarten und Formate bei ihm gut funktionieren —
 * basierend auf echten Engagement-Daten der letzten 3 Monate.
 *
 * Design-Entscheidungen:
 * - Kein leerer Zustand: bei has_data=false rendert die Komponente gar nichts
 * - Standardmäßig zugeklappt — nimmt keine Aufmerksamkeit wenn nicht relevant
 * - Kein Fachjargon: keine "Faktoren", keine Prozentzahlen, kein "Algorithmus"
 * - Schwache Kategorien werden neutral formuliert (nicht alarmierend)
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, TrendingUp, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Inhalts-Karte ─────────────────────────────────────────────────────────────

const InsightCard = ({ emoji, text, type }) => (
  <div
    className={`flex items-start gap-2.5 p-3 rounded-lg border text-sm ${
      type === "top"
        ? "bg-green-50 border-green-200 text-green-800"
        : "bg-stone-50 border-stone-200 text-stone-600"
    }`}
  >
    <span className="text-base flex-shrink-0 leading-snug">{emoji}</span>
    <p className="leading-relaxed">{text}</p>
  </div>
);

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const AnalyticsInsights = ({ platforms = ["instagram", "facebook"] }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(false);

  useEffect(() => {
    const platformStr = platforms.join(",");
    axios
      .get(`${API}/autopilot/insights?platforms=${platformStr}`)
      .then((res) => setData(res.data))
      .catch(() => { /* Silently ignore — non-critical */ })
      .finally(() => setLoading(false));
  }, [platforms.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kein Render wenn noch lädt oder keine Daten vorhanden
  if (loading || !data?.has_data) return null;

  const insights = data.customer_insights || {};
  const topItems = [
    ...(insights.top_pillars || []),
    ...(insights.top_formats || []),
  ];
  const weakItems = [
    ...(insights.weak_pillars || []),
    ...(insights.weak_formats || []),
  ];
  const bestTimes = data.best_posting_times || {};

  // Nichts anzeigen wenn es keinerlei Insights gibt (alle Faktoren neutral)
  const hasAnything = topItems.length > 0 || weakItems.length > 0 || Object.keys(bestTimes).length > 0;
  if (!hasAnything) return null;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 overflow-hidden">
      {/* Toggle-Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-blue-100 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-blue-800">
          <TrendingUp size={15} className="text-blue-500" />
          Was bei dir gut läuft
          <span className="text-xs font-normal text-blue-500 ml-1">
            ({data.post_count} Posts analysiert)
          </span>
        </span>
        <ChevronDown
          size={15}
          className={`text-blue-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Aufgeklappter Inhalt */}
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-blue-200 pt-4">
          {/* Top-Performer */}
          {topItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Funktioniert besonders gut
              </p>
              <div className="space-y-2">
                {topItems.map((item, i) => (
                  <InsightCard
                    key={i}
                    emoji={item.emoji}
                    text={item.text}
                    type="top"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Schwächere Kategorien */}
          {weakItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Etwas schwächer im Moment
              </p>
              <div className="space-y-2">
                {weakItems.map((item, i) => (
                  <InsightCard
                    key={i}
                    emoji={item.emoji}
                    text={item.text}
                    type="weak"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Beste Posting-Zeiten */}
          {Object.keys(bestTimes).length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Deine besten Posting-Zeiten
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(bestTimes).map(([platform, times]) => (
                  <div
                    key={platform}
                    className="flex items-center gap-2 bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-blue-800 capitalize">{platform}</span>
                    <span className="text-stone-400">·</span>
                    <span className="text-stone-600">{times.join(", ")} Uhr</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-blue-500 flex items-start gap-1.5">
                <Info size={11} className="flex-shrink-0 mt-0.5" />
                Der Autopilot plant neue Posts automatisch zu diesen Zeiten.
              </p>
            </div>
          )}

          {/* Datengrundlage */}
          <p className="text-xs text-blue-400 border-t border-blue-200 pt-3">
            {insights.summary || `Basierend auf deinen letzten ${data.post_count} Posts der vergangenen 3 Monate.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsInsights;
